import { useCallback, useEffect, useState } from 'react';
import cloudAssetService, {
  CloudProviderOption,
} from '../../services/cloudAssetService';

/**
 * Cloud provider master, shared by every cloud screen (User Story 92).
 *
 * Replaces the `CLOUD_PROVIDER_LABELS` constant that hardcoded AWS/Azure/GCP.
 * Four components need this list on the same page render, so the in-flight
 * request is cached module-side and they share one round trip; `refresh()`
 * clears it after an admin edits the master in Cloud Setup.
 *
 * A failed fetch is not surfaced as a toast — the caller's own load already
 * reports its errors, and an unresolved code falls back to displaying itself,
 * which is still readable.
 */
let cache: Promise<CloudProviderOption[]> | null = null;

function fetchProviders(): Promise<CloudProviderOption[]> {
  if (!cache) {
    cache = cloudAssetService.getProviders().catch((error) => {
      cache = null;
      throw error;
    });
  }
  return cache;
}

export function invalidateCloudProviders(): void {
  cache = null;
}

export function useCloudProviders() {
  const [providers, setProviders] = useState<CloudProviderOption[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    let active = true;
    setLoading(true);
    fetchProviders()
      .then((rows) => {
        if (active) setProviders(rows);
      })
      .catch(() => {
        if (active) setProviders([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => load(), [load]);

  const refresh = useCallback(() => {
    invalidateCloudProviders();
    load();
  }, [load]);

  /** Display name for a stored code; falls back to the code itself. */
  const providerLabel = useCallback(
    (code: string) =>
      providers.find((p) => p.code === code)?.name ?? code ?? '',
    [providers],
  );

  return { providers, providerLabel, loading, refresh };
}
